/**
 * TG-A / TG-B — presentational multi-series transaction chart. Renders a
 * given sample series (power kW + cumulative energy kWh, both auto-scaled, and
 * SoC % on a fixed 0–100 axis for DC sessions) as three SVG polylines plus a
 * live-value legend. Pure/stateless: the live chart (TransactionChart) and the
 * history drill-in both feed it a `samples` array.
 */
import { hasSoc, seriesMax, type TxSample } from '@/lib/txTelemetry';

const W = 320;
const H = 120;
const PAD = 4;
const INNER_W = W - PAD * 2;
const INNER_H = H - PAD * 2;

interface Props {
  readonly samples: readonly TxSample[];
  /** Right-aligned caption in the header (e.g. "this session", "paused…", a duration). */
  readonly note?: string;
  readonly testId?: string;
}

export function TxSeriesChart({ samples, note, testId }: Props): JSX.Element {
  const showSoc = hasSoc(samples);
  const powerMax = seriesMax(samples, 'powerW');
  const energyMax = seriesMax(samples, 'energyWh');
  const leftT = samples[0]?.t ?? 0;
  const rightT = samples[samples.length - 1]?.t ?? leftT;
  const spanMs = Math.max(1, rightT - leftT);

  const project = (s: TxSample, value: number, max: number): string => {
    const x = PAD + ((s.t - leftT) / spanMs) * INNER_W;
    const y = PAD + INNER_H - Math.min(1, max <= 0 ? 0 : value / max) * INNER_H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  };
  const line = (pick: (s: TxSample) => number, max: number): string =>
    samples.map((s) => project(s, pick(s), max)).join(' ');

  const latest = samples[samples.length - 1];
  const kw = ((latest?.powerW ?? 0) / 1000).toFixed(1);
  const kwh = ((latest?.energyWh ?? 0) / 1000).toFixed(3);
  const soc = latest?.socPct;

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-4"
      aria-label="Transaction chart"
      {...(testId !== undefined ? { 'data-testid': testId } : {})}
    >
      <header className="mb-3 flex items-baseline justify-between text-sm">
        <h2 className="font-medium text-slate-100">Transaction chart</h2>
        {note !== undefined ? <span className="text-xs text-slate-400">{note}</span> : null}
      </header>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-hidden="true">
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} className="stroke-slate-700" />
        <line x1={PAD} y1={PAD} x2={W - PAD} y2={PAD} className="stroke-slate-800" strokeDasharray="2 3" />
        {samples.length >= 2 ? (
          <>
            <polyline
              points={line((s) => s.powerW, powerMax)}
              fill="none"
              strokeWidth={1.5}
              className="stroke-primary-base"
              data-testid="tx-series-power"
            />
            <polyline
              points={line((s) => s.energyWh, energyMax)}
              fill="none"
              strokeWidth={1.5}
              className="stroke-amber-400"
              data-testid="tx-series-energy"
            />
            {showSoc ? (
              <polyline
                points={line((s) => s.socPct ?? 0, 100)}
                fill="none"
                strokeWidth={1.5}
                className="stroke-sky-400"
                data-testid="tx-series-soc"
              />
            ) : null}
          </>
        ) : null}
      </svg>
      <ul className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-300">
        <li className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 bg-primary-base" /> Power: {kw} kW
        </li>
        <li className="flex items-center gap-1">
          <span className="inline-block h-2 w-3 bg-amber-400" /> Energy: {kwh} kWh
        </li>
        {showSoc ? (
          <li className="flex items-center gap-1">
            <span className="inline-block h-2 w-3 bg-sky-400" /> SoC:{' '}
            {soc !== null && soc !== undefined ? `${soc.toFixed(1)}%` : '—'}
          </li>
        ) : null}
      </ul>
    </section>
  );
}
